import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FormField,
  Input,
  Button,
  Alert,
  Card,
  useToast
} from '../ui';
import { validationUtils, commonSchemas } from '../../utils';
import { useAuth } from '../../hooks/useAuth';

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  showLogin?: boolean;
  className?: string;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSuccess,
  onError,
  showLogin = true,
  className
}) => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { sendForgotPassword, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  // add near other state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);


  // Create validator for forgot password form
  const validator = validationUtils.createValidator(commonSchemas.forgotPassword);

  const { success } = useToast()

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) setEmailError('');
    if (generalError) setGeneralError('');
  };

  const validateForm = (): boolean => {
    const validationResult = validator.validate({ email });
    if (!validationResult.isValid) {
      const fieldErrors = validationResult.fieldErrors?.email;
      if (fieldErrors && fieldErrors.length > 0 && fieldErrors[0]) {
        setEmailError(fieldErrors[0].message);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setGeneralError('');

      const res = await sendForgotPassword(email);
      setIsSubmitted(true);
      success(res?.message || "Reset link sent successfully")

      // onSuccess?.();  // keeping behavior unchanged (commented as in your code)
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('forgotPasswordFailed', {
            defaultValue: 'Unable to send the reset link. Please try again.'
          });
      setGeneralError(errorMessage);
      onError?.(error as Error);
    }
  };

  const handleLogin = () => navigate('/login');

  const handleResend = async () => {
    if (!email) {
      setGeneralError(t('emailRequired', { defaultValue: 'Email address is required' }));
      return;
    }

    try {
      setGeneralError('');
      setResendSuccess(false);
      setResendLoading(true);
      const res = await sendForgotPassword(email);

      setIsSubmitted(true);
      setResendSuccess(true);
      success(res?.message || "Reset link resent successfully")
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : t('forgotPasswordFailed', {
            defaultValue: 'Unable to send the reset link. Please try again.'
          });
      setGeneralError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };


  if (isSubmitted) {
    return (
      <Card className={`bg-transparent dark:bg-transparent rounded-none border-0 shadow-none p-0 max-w-xl w-full mx-auto ${className || ''}`}>
        <div className="p-0">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('checkYourEmail', { defaultValue: 'Check your email' })}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('passwordResetSent', {
                email,
                defaultValue: 'We sent a password reset link to {{email}}.'
              })}
            </p>
          </div>

          <div className="space-y-4">

            <Button
              className="w-auto bg-primary-button hover:bg-primary-button font-bold text-md"
              onClick={handleResend}
              fullWidth
              loading={resendLoading}
              disabled={resendLoading || isLoading}
            >
              {resendLoading
                ? t('sending', { defaultValue: 'Sending...' })
                : t('resendEmail', { defaultValue: 'Resend email' })}
            </Button>



            {showLogin && (
              <button
                type="button"
                onClick={handleLogin}
                className="w-full text-sm font-semibold underline text-primary-button hover:text-primary-button dark:text-primary-button dark:hover:text-primary-button disabled:opacity-50"
                disabled={isLoading}
              >
                {t('backToLogin', { defaultValue: 'Back to sign in' })}
              </button>
            )}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-transparent dark:bg-transparent">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t('passwordResetInfo', {
                defaultValue:
                  'If you do not see the email, check your spam folder or resend the link.'
              })}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Main form — mirror Login page spacing, input style, button style, and link style
  return (
    <Card className={`bg-transparent dark:bg-transparent rounded-none border-0 shadow-none p-0 max-w-xl w-full mx-auto ${className || ''}`}>
      <div className="p-0">
        {/* Header (kept visible here; your Login header is commented out – UI shell remains the same) */}
        {/* <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('forgotPassword')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('forgotPasswordDescription')}
          </p>
        </div> */}

        {/* General Error */}
        {generalError && (
          <Alert variant="error" className="mb-4">
            {generalError}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <FormField
            label={t('email', { defaultValue: 'Email address' })}
            required
            error={emailError}
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder={t('emailPlaceholder', {
                defaultValue: 'Enter your email address'
              })}
              disabled={isLoading}
              autoFocus
              autoComplete="email"
              className="py-5 bg-transparent dark:bg-transparent"
            />
          </FormField>

          <Button
            type="submit"
            size="md"
            loading={isLoading}
            fullWidth
            disabled={isLoading}
            className="w-auto bg-primary-button hover:bg-primary-button font-bold text-md"
          >
            {isLoading
              ? t('sending', { defaultValue: 'Sending...' })
              : t('sendResetLink', { defaultValue: 'Send reset link' })}
          </Button>

          {/* Back to Login link (same style as Login's Forgot Password link) */}
          {showLogin && (
            <div className="flex items-center justify-between">
              <span /> {/* spacer to align like Login's left area */}
              <button
                type="button"
                onClick={handleLogin}
                disabled={isLoading}
                className="text-sm font-semibold underline text-primary-button hover:text-primary-button dark:text-primary-button dark:hover:text-primary-button disabled:opacity-50"
              >
                {t('backToLogin', { defaultValue: 'Back to sign in' })}
              </button>
            </div>
          )}
        </form>
      </div>
    </Card>
  );
};
